import React from 'react';
import {
  canManuallyMarkPaid,
  getAttentionLabel,
  getOrderAttentionReason,
  ORDER_SEGMENTS,
} from './orderFilters';

function orderProduct(order) {
  return order.product || order.Product || {};
}

function orderSubscriber(order) {
  return order.subscriber || order.PhoneNumber || {};
}

function orderDate(order, field) {
  const formattedField = `${field}Formatted`;
  if (order[formattedField]) return order[formattedField];
  if (!order[field]) return '—';
  return new Date(order[field]).toLocaleString();
}

function CustomerCell({order, includeEmail = false}) {
  const subscriber = orderSubscriber(order);
  const nickname = subscriber.nickname || 'Unknown customer';
  const nicknameContent = subscriber.id
    ? <a href={`/account/subscribers/${subscriber.id}`}>{nickname}</a>
    : nickname;

  return (
    <>
      <td data-label="Nickname">{nicknameContent}</td>
      {includeEmail && <td data-label="Email">{subscriber.email || '—'}</td>}
    </>
  );
}

function ProductCell({order}) {
  const product = orderProduct(order);
  return (
    <td data-label="Product">
      {product.name || 'Unknown product'} &times; {order.quantity || 1}
    </td>
  );
}

function AmountCell({order}) {
  return (
    <td data-label="Amount">
      {order.formattedTotal || '—'} {order.currency || ''}
    </td>
  );
}

function OrdersTable({
  orders = [],
  variant = ORDER_SEGMENTS.PAID,
  busyOrderId = null,
  onMarkPaid,
  onDelete,
}) {
  if (orders.length === 0) return null;

  if (variant === ORDER_SEGMENTS.ATTENTION) {
    return (
      <div className="orders-table-wrapper">
        <table className="orders-table orders-table--attention">
          <thead>
            <tr>
              <th>Created/View</th>
              <th>Nickname</th>
              <th>Product</th>
              <th>Amount</th>
              <th>Attention Needed</th>
            </tr>
          </thead>
          <tbody>
            {orders.map(order => (
              <tr key={order.uuid || order.id}>
                <td data-label="Created">
                  <a className="bold" href={`/account/orders/${order.uuid}`}>
                    {orderDate(order, 'createdAt')} - View Details
                  </a>
                </td>
                <CustomerCell order={order} />
                <ProductCell order={order} />
                <AmountCell order={order} />
                <td data-label="Attention">
                  <a
                    className={getOrderAttentionReason(order) === 'payment-pending'
                      ? 'warning'
                      : 'bold'}
                    href={`/account/orders/${order.uuid}`}
                  >
                    {getAttentionLabel(order)}
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const isUnpaid = variant === ORDER_SEGMENTS.UNPAID;
  return (
    <div className="orders-table-wrapper">
      <table className={`orders-table orders-table--${variant}`}>
        <thead>
          <tr>
            <th>Created/View</th>
            <th>Nickname</th>
            <th>Email</th>
            <th>Product</th>
            <th>Amount</th>
            {isUnpaid ? (
              <>
                <th>Mark as Paid</th>
                <th>Delete</th>
              </>
            ) : (
              <th>Valid Until</th>
            )}
          </tr>
        </thead>
        <tbody>
          {orders.map(order => {
            const isBusy = busyOrderId === order.id;
            return (
              <tr key={order.uuid || order.id}>
                <td data-label="Created">
                  <a className={isUnpaid ? 'bold' : ''} href={`/account/orders/${order.uuid}`}>
                    {orderDate(order, 'createdAt')}{isUnpaid ? ' - View Details' : ''}
                  </a>
                </td>
                <CustomerCell order={order} includeEmail />
                <ProductCell order={order} />
                <AmountCell order={order} />
                {isUnpaid ? (
                  <>
                    <td data-label="Mark">
                      {canManuallyMarkPaid(order) ? (
                        <button
                          type="button"
                          disabled={isBusy}
                          onClick={() => onMarkPaid?.(order)}
                        >
                          {isBusy ? 'Working…' : 'Mark as Paid'}
                        </button>
                      ) : (
                        <span className="help">Paid via processor</span>
                      )}
                    </td>
                    <td data-label="Delete">
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => onDelete?.(order)}
                      >
                        Delete
                      </button>
                    </td>
                  </>
                ) : (
                  <td data-label="Valid until">
                    {order.validUntil ? orderDate(order, 'validUntil') : '—'}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default OrdersTable;
